// These parallel with API SaleType choices.
import { ScreenTypes } from "constants/screen";

export enum SaleType {
  BOBBYCALF = "Bobby Calf",
  CLEARING = "Clearing Sale",
  EXPORT = "Export",
  EXTERNAL_AGENT_SALE = "External Agent Sale",
  SELL_VIA_EXTERNAL_AGENT = "Sell via External Agent",
  ON_FARM_AUCTION = "On Farm Auction",
  OVER_HOOKS = "Over Hooks",
  PADDOCK = "Paddock",
  PRIVATE_WEIGH = "Private Weigh",
  SALEYARD_AUCTION = "Saleyard Auction",
  SIGHTING = "Sighting",
  SUNDRY_SALE = "Sundry Sale",
  REFERENCE_SALE = "Reference Sale",
}

export const MarketDesignations = {
  PRIME: "Prime",
  STORE: "Store",
  OTHER: null,
};

export type MarketDesignationType = ValueInObj<typeof MarketDesignations>;

export const ParentSaleTabs = {
  BOUGHT: "BOUGHT",
  SOLD: "SOLD",
} as const;

export type ParentSaleTabType = ValueInObj<typeof ParentSaleTabs>;

export const UnselectableSaleTypes = [SaleType.REFERENCE_SALE];

export const SaleTypeVocabulary = {
  [SaleType.OVER_HOOKS]: {
    "Receival Ref": "Vendor Notes",
    "Sale date": "Kill date",
    Consignment: "Vendor",
    Consignments: "Vendors",
  },
  [SaleType.PADDOCK]: {
    "Receival Ref": "Vendor Notes",
    Consignment: "Vendor",
    Consignments: "Vendors",
  },
  [SaleType.CLEARING]: {
    "Receival Ref": "Vendor Notes",
    Consignment: "Vendor",
    Consignments: "Vendors",
    "Head Count": "Quantity/Units",
    "Avg/Head": "Avg/Unit",
    "Price per Head": "Price per Unit",
  },
  [SaleType.ON_FARM_AUCTION]: {
    "Receival Ref": "Vendor Notes",
  },
  [SaleType.BOBBYCALF]: {
    "Sale Lot": "Consignment",
    "Sale Lots": "Consignments",
  },
};

// Grouped sale types that are often seen together.
export const SaleyardSaleTypes = [SaleType.EXPORT, SaleType.SALEYARD_AUCTION];

export const LivestockAuctionSaleTypes: SaleType[] = [
  SaleType.EXTERNAL_AGENT_SALE,
  SaleType.EXPORT,
  SaleType.ON_FARM_AUCTION,
  SaleType.PRIVATE_WEIGH,
  SaleType.SALEYARD_AUCTION,
  SaleType.SELL_VIA_EXTERNAL_AGENT,
];

export const LivestockAuctionAndClearanceSaleTypes = [
  ...LivestockAuctionSaleTypes,
  SaleType.CLEARING,
];

export const LivestockAuctionAndPaddockSaleTypes = [
  ...LivestockAuctionSaleTypes,
  SaleType.PADDOCK,
];

export const LivestockSaleTypes = [
  ...LivestockAuctionSaleTypes,
  SaleType.OVER_HOOKS,
  SaleType.PADDOCK,
];

export const ConsignableSaleTypes = [
  SaleType.CLEARING,
  SaleType.EXPORT,
  SaleType.ON_FARM_AUCTION,
  SaleType.PRIVATE_WEIGH,
  SaleType.SALEYARD_AUCTION,
];

export const TransactionalSaleTypes = [SaleType.OVER_HOOKS, SaleType.PADDOCK];

export const NlisTakeAndSellSaleTypes = [
  SaleType.SALEYARD_AUCTION,
  SaleType.BOBBYCALF,
  SaleType.PRIVATE_WEIGH,
];

export const VendorBillableSalesTypes = [
  ...LivestockSaleTypes,
  SaleType.CLEARING,
  SaleType.SIGHTING,
  // NOT BOBBYCALF - no billing
  // NOT SUNDRY_SALE - vendors not relevant
  // NOT REFERENCE_SALE - vendors not relevant
];

export const ConsignmentExceptions = {
  NO_PIC: "NO PIC",
  NVD_ID_MISSING: "NVD ID MISSING",
  NVD_SCAN_MISSING: "NVD SCAN MISSING",
  NVD_INCOMPLETE: "NVD INCOMPLETE",
  RESTRICTED_PIC: "RESTRICTED PIC",
  BLOCKED_INACTIVE_PIC: "BLOCKED/INACTIVE PIC",
  // Scan Rate Compliance is an exception for Cattle, but a warning for other species
  SCAN_RATE_COMPLIANCE: "SCAN RATE COMPLIANCE",
  UNBALANCED: "UNBALANCED",
};

export const CONSIGNMENT_NVD_EXCEPTIONS = [
  ConsignmentExceptions.NO_PIC,
  ConsignmentExceptions.NVD_ID_MISSING,
  ConsignmentExceptions.NVD_SCAN_MISSING,
  ConsignmentExceptions.NVD_INCOMPLETE,
  ConsignmentExceptions.RESTRICTED_PIC,
];

export const ConsignmentWarnings = {
  NVD_HC_MISMATCH: "NVD HC MISMATCH",
  // Scan Rate Compliance is an exception for Cattle, but a warning for other species
  SCAN_RATE_COMPLIANCE: "SCAN RATE COMPLIANCE",
  CONSIGNMENT_SCANS_FOUND: "CONSIGNMENT SCANS FOUND",
  UNREGISTERED_EID: "UNREGISTERED EID",
};

export const SaleLotException = {
  BLOCKED_INACTIVE_PIC: "BLOCKED/INACTIVE PIC",
  NO_AGE: "NO AGE",
  NO_BREED: "NO BREED",
  NO_PIC: "NO PIC",
  NO_SEX: "NO SEX",
  RESTRICTED_PIC: "RESTRICTED PIC",
  UNBALANCED: "UNBALANCED",
  NO_WEIGHT_RANGE: "NO WEIGHT RANGE",
  NO_DENTITION: "NO DENTITION",
};

export const SaleLotWarning = {
  NO_RECIPIENTS: "NO EMAIL RECIPIENTS",
  SINGLE_WEIGH_SUM: "SINGLE WEIGH WEIGHT NOT EQUAL TO SALE LOT WEIGHT",
  THIRD_PARTY_EMAIL_MISSING: "THIRD PARTY EMAIL MISSING",
  UNREGISTERED_EID: "UNREGISTERED EID",
  NO_BREED: "NO BREED",
  NO_SEX: "NO SEX",
  NO_AGE: "NO AGE",
  VENDOR_SPLIT_LOTS_UNBALANCED: "VENDOR SPLIT LOTS UNBALANCED",
  PRIMARY_VENDOR_IS_NOT_PART_OF_VENDOR_SPLIT_LOTS:
    "PRIMARY VENDOR IS NOT PART OF VENDOR SPLIT LOTS",
  NO_WEIGHT_RANGE: "NO WEIGHT RANGE",
  NO_DENTITION: "NO DENTITION",
};

export const consignmentStatuses = {
  BOOKED: "BOOKED",
  ARRIVED: "ARRIVED",
};

export const consignmentStatusesOrder = [
  consignmentStatuses.BOOKED,
  consignmentStatuses.ARRIVED,
];

export const saleLotStatuses = {
  NOT_PENNED: "NOT PENNED",
  NOT_COUNTED: "NOT COUNTED",
  PENNED: "PENNED",
  NOT_SOLD: "NOT SOLD",
  PARTIALLY_DELIVERED: "PARTIALLY DELIVERED",
  SOLD: "SOLD",
  NO_SALE: "NO SALE",
  DELIVERED: "DELIVERED",
};

export const saleLotStatusesOrder = [
  saleLotStatuses.NOT_COUNTED,
  saleLotStatuses.NOT_PENNED,
  saleLotStatuses.PENNED,
  saleLotStatuses.NOT_SOLD,
  saleLotStatuses.SOLD,
  saleLotStatuses.NO_SALE,
  saleLotStatuses.DELIVERED,
];

export const NominationStatus = {
  ARRIVED: "NOMINATION_ARRIVED",
  NOMINATED: "NOMINATION_NOMINATED",
};

export const NominationStatusText = {
  [NominationStatus.ARRIVED]: "Arrived",
  [NominationStatus.NOMINATED]: "Nominated",
};

export const NominationStatusColor = {
  [NominationStatus.ARRIVED]: "success",
  [NominationStatus.NOMINATED]: "warning",
};

export const auctionPenActions = {
  RE_PEN: "RE_PEN",
};

export const cards = {
  AUCTION_PEN_CARD: "AUCTION_PEN_CARD",
  BUYER_CARD: "BUYER_CARD",
  BUYER_WAY_CARD: "BUYER_WAY_CARD",
  CONSIGNMENT_CARD: "CONSIGNMENT_CARD",
  SALE_LOT_CARD: "SALE_LOT_CARD",
  TEMP_PEN_CARD: "TEMP_PEN_CARD",
};

export const SaleRoundName = {
  BobbyCalf: "BobbyCalf",
  ExternalAgentSale: "External Agent Sale",
  SellViaExternalAgent: "Sell via External Agent",
};

export const SALE_VALIDATION_ERRORS = {
  DUPLICATE_SALE: "DUPLICATE_SALE",
};

export const SystemSaleyards = [
  SaleType.CLEARING,
  SaleType.EXTERNAL_AGENT_SALE,
  SaleType.OVER_HOOKS,
  SaleType.PADDOCK,
  SaleType.SELL_VIA_EXTERNAL_AGENT,
];

export const NonBuyerWaySaleTypes = [SaleType.SUNDRY_SALE];

export const NonConsignableSaleyards = [SaleType.OVER_HOOKS, SaleType.PADDOCK];

export const SaleStatus = {
  DRAFT: "DRAFT",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  ARCHIVED: "ARCHIVED",
  REFERENCE: "REFERENCE",
  PAST: "PAST",
  FUTURE: "FUTURE",
} as const;

export type SaleStatusType = ValueInObj<typeof SaleStatus>;
export type SaleStatusArchetype = Exclude<SaleStatusType, "PAST" | "FUTURE">;

export const SelectableSaleStatuses = [
  SaleStatus.DRAFT,
  SaleStatus.IN_PROGRESS,
  SaleStatus.COMPLETED,
  SaleStatus.ARCHIVED,
] as const;

export const SaleStatusLabels = {
  [SaleStatus.DRAFT]: "Draft",
  [SaleStatus.IN_PROGRESS]: "In Progress",
  [SaleStatus.COMPLETED]: "Completed",
  [SaleStatus.ARCHIVED]: "Archived",
  [SaleStatus.REFERENCE]: "Reference",
  [SaleStatus.PAST]: "Past",
  [SaleStatus.FUTURE]: "Future",
} as const;

export const SaleStatusColors = {
  [SaleStatus.DRAFT]: "grayAE", // Gray
  [SaleStatus.IN_PROGRESS]: "primary", // Blue
  [SaleStatus.COMPLETED]: "lightGreen", // Green
  [SaleStatus.ARCHIVED]: "gray40", // Dark Gray
  [SaleStatus.REFERENCE]: "gray8C", // Light Gray
  [SaleStatus.PAST]: "gray8C", // Light Gray
  [SaleStatus.FUTURE]: "pending", // Orange
} as const;

export const TabsForSaleType = {
  [SaleType.EXTERNAL_AGENT_SALE]: {
    [ScreenTypes.DESKTOP]: [
      "consignments",
      "salelots",
      "buyer-cards",
      "eids",
      "reports",
      "billing",
      "overview",
    ],
    [ScreenTypes.MOBILE]: [
      "overview",
      "consignment-cards",
      "eids",
      "reports",
      "buyer-cards",
      "auction-pen-cards",
      "billing",
    ],
  },
  [SaleType.SELL_VIA_EXTERNAL_AGENT]: {
    [ScreenTypes.DESKTOP]: [
      "consignments",
      "salelots",
      "buyer-cards",
      "eids",
      "reports",
      "billing",
      "overview",
    ],
    [ScreenTypes.MOBILE]: [
      "overview",
      "consignment-cards",
      "eids",
      "reports",
      "buyer-cards",
      "auction-pen-cards",
      "billing",
    ],
  },
};

export const filterTabsBySaleType = (saleType, group) => t => {
  const hasExplicit = TabsForSaleType?.[saleType]?.[group];
  if (hasExplicit) {
    return hasExplicit.includes(t.id);
  }
  return true;
};

export const WarningLevel = {
  WARNING: "Warning",
  EXCEPTION: "Exception",
  WARNING_AND_BLOCK: "Warning & Block",
};

export const LOAD_MORE_SALES = "Load more sales...";

// Matching Prod
export const SaleSubTypeName = {
  INTEREST: "INTEREST",
};
